import * as THREE from 'three/webgpu';
import {isSketchPointObject, type SketchPointObject} from '../../mesh/sketch';
import type {PointSelectionCandidate} from '../../reference/reference-point/ReferencePointController';
import {SketchEntitiesTransformSelectionRole} from '../../sketch/entities-transform';
import type {RawPointerEventPayload} from '../../viewer/moudles/EventManager';
import type {EntitySelectionManager} from '../EntitySelectionManager';
import {AbstractSketchEntitiesTransformSelectionHandler} from './AbstractSketchEntitiesTransformSelectionHandler';

/**
 * 圆周草图阵列选择处理器。
 *
 * 源实体和可跳过实例使用草图实体拾取链路；
 * 阵列中心只使用活动草图内的控制点或草图点精确拾取链路。
 */
export class SketchCircularPatternSelectionHandler extends AbstractSketchEntitiesTransformSelectionHandler {
    /**
     * 创建圆周草图阵列选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 处理鼠标按下。
     *
     * SOLIDWORKS 草图阵列的可跳过实例不支持框选；激活该槽位时不建立框选起点，
     * 其余选择角色继续复用草图实体变换的统一框选行为。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (this.isSkipSelectionActive()) {
            return;
        }
        super.onMouseDown(payload);
    }

    /**
     * 处理鼠标移动。
     *
     * 阵列中心槽位只允许活动草图点进入悬停反馈，
     * 禁止复用公共旋转中心的“草图平面任意点”预览。
     *
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (!this.isCenterSelectionActive()) {
            super.onMouseMove(payload);
            return;
        }
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        const hoveredPick = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectCenterPointTargets(),
        );
        this.updateHoverState(hoveredPick);
    }

    /**
     * 处理鼠标点击。
     *
     * 阵列中心槽位直接将命中的草图点对象交给控制器；
     * 点击空白位置、线、圆或其他对象时不改变中心。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (!this.isCenterSelectionActive()) {
            super.onClick(payload);
            return;
        }
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const picked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectCenterPointTargets(),
        );
        if (!picked || !isSketchPointObject(picked.object)) {
            return;
        }
        this.entitySelectionManager.renderer.sketchCircularPatternController
            ?.handleCenterPointSelection(picked.object);
    }

    /**
     * 将阵列选择角色映射为公共草图实体变换选择角色。
     *
     * @returns 公共选择角色。
     */
    protected resolveSelectionRole(): SketchEntitiesTransformSelectionRole {
        const role = this.entitySelectionManager.renderer.sketchCircularPatternController
            ?.getSnapshot().activeSelectionRole;
        return role === 'center'
            ? SketchEntitiesTransformSelectionRole.RotationCenter
            : SketchEntitiesTransformSelectionRole.Entities;
    }

    /**
     * 收集当前点击候选。
     *
     * 跳过实例槽位只允许命中红色或洋红色锚点，禁止点击黄色几何本体或源实体改变跳过集合。
     *
     * @returns 当前模式点击候选。
     */
    protected override collectClickSelectionTargets(): THREE.Object3D[] {
        if (this.isSkipSelectionActive()) {
            return this.entitySelectionManager.renderer.sketchCircularPatternController
                ?.getSkipInstanceMarkerObjects() ?? [];
        }
        return super.collectClickSelectionTargets();
    }

    /**
     * 收集当前悬停候选。
     *
     * @returns 当前模式悬停候选。
     */
    protected override collectHoverSelectionTargets(): THREE.Object3D[] {
        if (this.isSkipSelectionActive()) {
            return this.entitySelectionManager.renderer.sketchCircularPatternController
                ?.getSkipInstanceMarkerObjects() ?? [];
        }
        return super.collectHoverSelectionTargets();
    }

    /**
     * 根据屏幕坐标拾取当前圆周阵列交互对象。
     *
     * 设计说明：
     * 1. 跳过标记是命令态覆盖对象，不是正式草图实体；
     * 2. 统一实体拾取会按领域守卫过滤普通 Sprite，因此该槽位直接复用底层屏幕精确点拾取；
     * 3. 其他选择角色仍走统一实体拾取，不扩大临时标记的影响范围。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 当前可交互对象。
     * @returns 命中的标记或统一实体；未命中时返回 null。
     */
    protected override pickEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!this.isSkipSelectionActive() && !this.isCenterSelectionActive()) {
            return super.pickEntityAt(clientX, clientY, interactableObjects);
        }

        const container = this.entitySelectionManager.renderer.container;
        const camera = this.entitySelectionManager.renderer.camera;
        if (!container || !camera) {
            return null;
        }

        const markerObjects = interactableObjects.filter(
            (object): object is THREE.Sprite => object instanceof THREE.Sprite,
        );
        if (markerObjects.length === 0) {
            return null;
        }

        const containerRect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - containerRect.left) / containerRect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - containerRect.top) / containerRect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouseNdc, camera);

        return this.entitySelectionManager.screenSpacePrecisionPicker.pickNearestPoint({
            points: markerObjects,
            camera,
            containerRect,
            clientX,
            clientY,
            ray: this.raycaster.ray,
        });
    }

    /**
     * 处理实体点选。
     *
     * @param object 命中对象。
     */
    protected handleEntityPicked(object: THREE.Object3D): void {
        this.entitySelectionManager.renderer.sketchCircularPatternController?.handleEntityPick(object);
    }

    /**
     * 处理源实体框选。
     *
     * @param objects 框选对象。
     */
    protected handleEntitiesBoxSelected(objects: readonly THREE.Object3D[]): void {
        this.entitySelectionManager.renderer.sketchCircularPatternController?.handleEntityBoxSelection(objects);
    }

    /**
     * 处理阵列中心参考点。
     *
     * @param candidate 参考点候选。
     */
    protected handleReferenceCandidate(candidate: PointSelectionCandidate): void {
        this.entitySelectionManager.renderer.sketchCircularPatternController
            ?.handleReferencePointSelection(candidate);
    }

    /**
     * 判断当前是否处于可跳过实例逐点选择槽位。
     *
     * @returns 激活可跳过实例槽位时返回 true。
     */
    private isSkipSelectionActive(): boolean {
        return this.entitySelectionManager.renderer.sketchCircularPatternController
            ?.getSnapshot().activeSelectionRole === 'skipped-instances';
    }

    /**
     * 判断当前是否处于阵列中心点选槽位。
     *
     * @returns 激活阵列中心槽位时返回 true。
     */
    private isCenterSelectionActive(): boolean {
        return this.entitySelectionManager.renderer.sketchCircularPatternController
            ?.getSnapshot().activeSelectionRole === 'center';
    }

    /**
     * 收集当前活动草图中可作为阵列中心的点对象。
     *
     * 只从活动 `SketchGroup` 的点索引取值，从候选集源头排除线、圆、特征点与其他草图的点。
     *
     * @returns 活动草图点列表。
     */
    private collectCenterPointTargets(): SketchPointObject[] {
        const sketchGroup = this.entitySelectionManager.renderer.sketchGroupManager?.activeSketchGroup;
        return sketchGroup ? Array.from(sketchGroup.getSketchPointObjects()) : [];
    }
}
